import { z } from "zod";
import { renommeeFicheSchema } from "./viewers";

/**
 * Les lectures de la regie En direct (lot Q1, tache E.1 et E.4 bis) : l'instantane du direct, la fiche
 * d'un viewer et ses messages. **Toutes les dates restent en ISO 8601 (UTC) dans l'API** : c'est le
 * frontal qui les ecrit en toutes lettres.
 *
 * Aucun identifiant n'est un nombre : un `bigint` de la base voyage en texte.
 */

/** Le statut d'un present : nouveau (jamais venu), revient (absent depuis plus de 30 jours), habitue. */
export const statutPresentSchema = z.enum(["nouveau", "revient", "habitue"]);
export type StatutPresent = z.infer<typeof statutPresentSchema>;

/** `GET /api/direct/etat` : tout ce que l'ecran En direct montre, en une lecture. */
export const etatDirectSchema = z.object({
  /** Le stream ouvert, ou `null` hors direct. `manuel` : ouvert a la main depuis la regie (sans identifiant Twitch). */
  stream: z
    .object({
      id: z.string(),
      debut: z.string(),
      titre: z.string().nullable(),
      jeu: z.string().nullable(),
      manuel: z.boolean(),
    })
    .nullable(),
  compteurs: z.object({
    presents: z.number().int(),
    pic: z.number().int(),
    messages: z.number().int(),
    uniques: z.number().int(),
    follows: z.number().int(),
    subs: z.number().int(),
    bits: z.number().int(),
    raids: z.number().int(),
  }),
  presents: z.array(
    z.object({
      id: z.string(),
      nom: z.string(),
      avatar: z.string().nullable(),
      minutes: z.number().int(),
      messages: z.number().int(),
      statut: statutPresentSchema,
    }),
  ),
  /** Dix au plus. */
  topChatters: z.array(z.object({ id: z.string(), nom: z.string(), messages: z.number().int() })),
  /** Trente au plus, du plus recent au plus ancien. */
  derniers: z.array(
    z.object({
      id: z.string(),
      type: z.string(),
      survenuLe: z.string(),
      nom: z.string().nullable(),
    }),
  ),
  scene: z.object({ nom: z.string(), depuis: z.string() }).nullable(),
  overlays: z.array(z.object({ nom: z.string(), depuis: z.string() })),
});
export type EtatDirect = z.infer<typeof etatDirectSchema>;

/** `GET /api/direct/viewers/{id}` : la fiche d'un viewer. */
export const ficheViewerSchema = z.object({
  id: z.string(),
  nom: z.string(),
  login: z.string(),
  avatar: z.string().nullable(),
  premierPassage: z.string(),
  streams: z.number().int(),
  minutes: z.number().int(),
  messages: z.number().int(),
  bits: z.number().int(),
  follow: z.string().nullable(),
  subs: z.number().int(),
  /** Ce que l'ancien Castellan en savait (import), ou `null` sans import. */
  ancien: z
    .object({
      premiereVenue: z.string().nullable(),
      streams: z.number().int(),
      messages: z.number().int(),
      bits: z.number().int(),
    })
    .nullable(),
  /** Vingt au plus. */
  derniers: z.array(z.object({ type: z.string(), survenuLe: z.string() })),
  /** Sa renommee et son rang (ticket 2 du socle), ou `null` : un viewer sans profil est Ecuyer a 0. */
  renommee: renommeeFicheSchema.nullable(),
});
export type FicheViewer = z.infer<typeof ficheViewerSchema>;

/** Un message du chat tel que la fiche le montre, rattache a son stream (ou a aucun). */
export const messageViewerSchema = z.object({
  id: z.string(),
  texte: z.string(),
  envoyeLe: z.string(),
  stream: z.object({ id: z.string(), debut: z.string(), titre: z.string().nullable() }).nullable(),
});
export type MessageViewer = z.infer<typeof messageViewerSchema>;

/** Le curseur d'une page de messages : l'horodatage a la microseconde, un tiret bas, l'identifiant. */
export const curseurMessagesSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z_\d{1,18}$/)
  .refine(dateDuCurseurValide, "Date de curseur impossible");

/** Vrai si la date du curseur existe vraiment (pas de 13e mois, de 30 fevrier, de 25e heure) : Postgres la refuserait en 500. */
function dateDuCurseurValide(curseur: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\./.exec(curseur);
  if (m === null) return false;
  const [annee, mois, jour, heure, minute, seconde] = m.slice(1).map(Number) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  const d = new Date(Date.UTC(annee, mois - 1, jour, heure, minute, seconde));
  return (
    d.getUTCFullYear() === annee &&
    d.getUTCMonth() === mois - 1 &&
    d.getUTCDate() === jour &&
    d.getUTCHours() === heure &&
    d.getUTCMinutes() === minute &&
    d.getUTCSeconds() === seconde
  );
}

/** La longueur d'une recherche dans les messages. */
export const RECHERCHE_MESSAGES_MAX = 100;

/** `GET /api/direct/viewers/{id}/messages?avant=&cherche=` */
export const messagesViewerQuerySchema = z.object({
  avant: curseurMessagesSchema.optional(),
  cherche: z.string().max(RECHERCHE_MESSAGES_MAX).optional(),
});

/** Une page de messages, du plus recent au plus ancien ; `suivant` est le curseur de la page d'apres, ou `null`. */
export const pageMessagesSchema = z.object({
  messages: z.array(messageViewerSchema),
  suivant: curseurMessagesSchema.nullable(),
});
export type PageMessages = z.infer<typeof pageMessagesSchema>;

/** L'identifiant d'un viewer dans une adresse : des chiffres (un `bigint`). */
export const idViewerParamSchema = z.object({ id: z.string().regex(/^\d{1,18}$/) });
